import type { ReactNode } from "react";

import { Icon } from "./Icon";

/**
 * Figma: `Check` внутри блока согласия (22:233) — квадрат 20×20, скругление 6,
 * в отмеченном состоянии залит акцентом с инверсной галочкой.
 *
 * Невыбранного состояния в макете нет: оба чекбокса нарисованы отмеченными.
 * Пустой квадрат сделан рамкой `border-strong` — тем же приёмом, которым в
 * системе оформлены все неактивные поверхности.
 *
 * Движение (FE-13): заливка не подменяется, а разворачивается из центра, и
 * следом галочка входит с перелётом. Оба слоя присутствуют в разметке всегда —
 * иначе браузеру нечего анимировать при появлении.
 *
 * Отклик (FE-14): `select`, а не `tap`. Чекбокс не «нажимают», им переводят
 * значение, и семейство `selectionChanged` — единственное, серия которого не
 * превращается в дробь.
 */
interface CheckboxProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  children: ReactNode;
}

export function Checkbox({ checked, onChange, children }: CheckboxProps) {
  return (
    <label data-haptic="select" className="flex cursor-pointer items-start gap-[9px]">
      <input
        type="checkbox"
        checked={checked}
        onChange={(event) => {
          onChange(event.target.checked);
        }}
        className="sr-only"
      />
      <span
        aria-hidden="true"
        className={[
          "relative flex size-5 shrink-0 items-center justify-center overflow-hidden rounded-md border border-solid",
          "transition-colors duration-[var(--motion-fast)] ease-standard",
          checked ? "border-action-primary" : "border-border-strong",
        ].join(" ")}
      >
        <span
          className={[
            "absolute inset-0 rounded-md bg-action-primary",
            "transition-transform duration-[var(--motion-fast)] ease-out-quint",
            checked ? "scale-100" : "scale-0",
          ].join(" ")}
        />
        <Icon
          name="check"
          size={12}
          className={[
            "relative text-text-inverse transition duration-[var(--motion-base)] ease-spring",
            checked ? "scale-100 opacity-100" : "scale-50 opacity-0",
          ].join(" ")}
        />
      </span>
      <span className="text-caption font-normal text-neutral-label">{children}</span>
    </label>
  );
}
